import type { TFunction } from 'i18next'
import type { ListEndpointsQuery } from '../../../types/case-container'

export interface ColumnDefinition {
  id: string
  label: string
  defaultVisible: boolean
  itSystemOnly?: boolean
}

export const getColumnDefinitions = (t: TFunction<'cases'>): ColumnDefinition[] => [
  { id: 'hostname', label: t('infrastructure.columns.hostname'), defaultVisible: true },
  { id: 'type', label: t('infrastructure.columns.type'), defaultVisible: true },
  { id: 'ipAddresses', label: t('infrastructure.columns.ipAddresses'), defaultVisible: true },
  {
    id: 'domain',
    label: t('infrastructure.columns.domain'),
    defaultVisible: true,
    itSystemOnly: true,
  },
  { id: 'os', label: t('infrastructure.columns.os'), defaultVisible: true, itSystemOnly: true },
  {
    id: 'osVersion',
    label: t('infrastructure.columns.osVersion'),
    defaultVisible: false,
    itSystemOnly: true,
  },
  {
    id: 'ouPath',
    label: t('infrastructure.columns.ouPath'),
    defaultVisible: false,
    itSystemOnly: true,
  },
  { id: 'importance', label: t('infrastructure.columns.importance'), defaultVisible: true },
  {
    id: 'roles',
    label: t('infrastructure.columns.roles'),
    defaultVisible: true,
    itSystemOnly: true,
  },
  { id: 'links', label: t('infrastructure.columns.links'), defaultVisible: true },
]

export const getColumnVisibilityKey = (caseId: string): string => `infrastructure-columns-${caseId}`

export interface FilterOption {
  value: string
  label: string
}

export interface FilterDefinition {
  id: keyof ListEndpointsQuery
  label: string
  options: FilterOption[]
}

export const getFilterDefinitions = (t: TFunction<'cases'>): FilterDefinition[] => [
  {
    id: 'deviceType',
    label: t('infrastructure.columns.type'),
    options: [
      { value: 'workstation', label: t('infrastructure.deviceTypes.workstation') },
      { value: 'server', label: t('infrastructure.deviceTypes.server') },
      { value: 'domain_controller', label: t('infrastructure.deviceTypes.domainController') },
      { value: 'file_server', label: t('infrastructure.deviceTypes.fileServer') },
      { value: 'mail_server', label: t('infrastructure.deviceTypes.mailServer') },
      { value: 'web_server', label: t('infrastructure.deviceTypes.webServer') },
      { value: 'database_server', label: t('infrastructure.deviceTypes.databaseServer') },
      { value: 'network_device', label: t('infrastructure.deviceTypes.networkDevice') },
      { value: 'firewall', label: t('infrastructure.deviceTypes.firewall') },
      { value: 'router', label: t('infrastructure.deviceTypes.router') },
      { value: 'switch', label: t('infrastructure.deviceTypes.switch') },
      { value: 'mobile_device', label: t('infrastructure.deviceTypes.mobileDevice') },
      { value: 'cloud_instance', label: t('infrastructure.deviceTypes.cloudInstance') },
      { value: 'virtual_machine', label: t('infrastructure.deviceTypes.virtualMachine') },
      { value: 'container', label: t('infrastructure.deviceTypes.container') },
      { value: 'iot_device', label: t('infrastructure.deviceTypes.iotDevice') },
    ],
  },
  {
    id: 'importance',
    label: t('infrastructure.columns.importance'),
    options: [
      { value: 'critical', label: t('infrastructure.importance.critical') },
      { value: 'high', label: t('infrastructure.importance.high') },
      { value: 'medium', label: t('infrastructure.importance.medium') },
      { value: 'low', label: t('infrastructure.importance.low') },
      { value: 'unknown', label: t('infrastructure.importance.unknown') },
    ],
  },
  {
    id: 'criticality',
    label: t('infrastructure.filters.criticality' as never),
    options: [
      { value: 'critical', label: t('infrastructure.importance.critical') },
      { value: 'high', label: t('infrastructure.importance.high') },
      { value: 'medium', label: t('infrastructure.importance.medium') },
      { value: 'low', label: t('infrastructure.importance.low') },
      { value: 'unknown', label: t('infrastructure.importance.unknown') },
    ],
  },
  {
    id: 'collectionStatus',
    label: t('infrastructure.filters.collectionStatus' as never),
    options: [
      { value: 'not_started', label: t('infrastructure.filters.notStarted' as never) },
      { value: 'pending', label: t('infrastructure.filters.pending' as never) },
      { value: 'in_progress', label: t('infrastructure.filters.inProgress' as never) },
      { value: 'completed', label: t('infrastructure.filters.completed' as never) },
      { value: 'failed', label: t('infrastructure.filters.failed' as never) },
      { value: 'not_required', label: t('infrastructure.filters.notRequired' as never) },
    ],
  },
  {
    id: 'hasOpenQuestion',
    label: t('infrastructure.filters.openQuestions' as never),
    options: [
      { value: 'true', label: t('infrastructure.filters.hasOpenQuestions' as never) },
      { value: 'false', label: t('infrastructure.filters.noOpenQuestions' as never) },
    ],
  },
]

export interface ActiveFilter {
  fieldId: keyof ListEndpointsQuery
  fieldLabel: string
  value: string
  valueLabel: string
}

export function filtersToQuery(filters: ActiveFilter[], search?: string): ListEndpointsQuery {
  const query: ListEndpointsQuery = {}
  if (search) {
    query.search = search
  }
  for (const filter of filters) {
    if (filter.fieldId === 'hasOpenQuestion') {
      query.hasOpenQuestion = filter.value === 'true'
    } else {
      ;(query as Record<string, unknown>)[filter.fieldId] = filter.value
    }
  }
  return query
}
